import React, { useEffect, useMemo, useState } from 'react';
import { Page } from '@shopify/polaris';
import { config } from '@/config';
import { AccountPlan, Subscription } from '@/constants/enum';
import { getAppParams } from '@/helpers';
import { useGetUserPlanQuery, useUpdateDiscountCodeMutation, useUpdatePlanMutation } from '@/redux/api/api.caller';
import { IPricingPlanData } from '@/types/dashboard';
import {
  ADDON_APPS,
  BUNDLE_APPS,
  DEFAULT_APP_STATES,
  discountCode as bundleDiscountCode,
  discountRate as bundleDiscountRate,
  IBundleApp,
  IBundlePlan,
} from './config';
import AppCard from './AppCard';
import CoBenefitBanner from './CoBenefitBanner';
import SummaryCard from './SummaryCard';
import TrakowCard from './TrakowCard';
import { useAddonPrices } from './useAddonPrices';
import { StyledBundleAside, StyledBundleLayout, StyledBundleMain } from './styled';

interface IBundleBuilderProps {
  onBack: () => void;
}

const formatQuota = (quota: string): string => {
  if (!quota || quota.toUpperCase() === 'UNLIMITED') return 'Unlimited shipments';
  const num = parseInt(quota, 10);
  return isNaN(num) ? quota : `${num.toLocaleString()} shipments/mo`;
};

const buildTrakowPlans = (apiPlans: IPricingPlanData[]): IBundlePlan[] => {
  return apiPlans
    .filter((p) => p.plan !== AccountPlan.Starter)
    .map((p) => ({
      id: p.plan,
      name: p.displayName,
      quota: formatQuota(p.quotaPerMonth),
      price: parseFloat(p.monthlyPrice) || 0,
    }));
};

const BundleBuilder = ({ onBack }: IBundleBuilderProps) => {
  const [appStates, setAppStates] = useState(DEFAULT_APP_STATES);
  const [planSelections, setPlanSelections] = useState({ trakow: '', paypal: '', returns: '' });

  const shop = config.shop || (getAppParams() as { shop?: string })?.shop || '';
  const { data: planData } = useGetUserPlanQuery({});
  const { paypalPlans, returnsPlans, currentPaypalPlan, currentReturnsPlan, paypalInstalled, returnsInstalled } =
    useAddonPrices(shop);

  const [updatePlan] = useUpdatePlanMutation();
  const [applyDiscount] = useUpdateDiscountCodeMutation();
  const [isChargingTrakow, setIsChargingTrakow] = useState(false);

  const isTrakowLoading = !planData?.data;
  const isPaypalLoading = paypalPlans === null;
  const isReturnsLoading = returnsPlans === null;

  const handleChargeTrakow = async () => {
    if (!planSelections.trakow || isChargingTrakow) return;
    setIsChargingTrakow(true);
    try {
      const addOnCount = Math.min((appStates.paypal ? 1 : 0) + (appStates.returns ? 1 : 0), 2) as 0 | 1 | 2;
      const code = bundleDiscountCode(bundleDiscountRate(addOnCount));

      // Apply the bundle discount code first so the charge is created at the discounted price
      if (code) {
        await applyDiscount({ discountCode: code, plan: planSelections.trakow as AccountPlan });
      }

      const res = await updatePlan({
        plan: planSelections.trakow as AccountPlan,
        subscription: Subscription.Monthly,
        extraQuotaPerMonth: 0,
      });

      if ('data' in res && res.data?.data?.confirmationUrl) {
        window.open(res.data.data.confirmationUrl, '_blank');
      } else {
        onBack();
        setIsChargingTrakow(false);
      }
    } catch {
      setIsChargingTrakow(false);
    } finally {
      setIsChargingTrakow(false);
    }
  };

  const dynamicTrakowApp = useMemo<IBundleApp>(() => {
    if (!planData?.data?.allPlans?.length) return { ...BUNDLE_APPS[0], isInstalled: true };
    const plans = buildTrakowPlans(planData.data.allPlans);
    return { ...BUNDLE_APPS[0], plans, currentPlanId: planData.data.plan, isInstalled: true };
  }, [planData]);

  const dynamicAddonApps = useMemo(() => {
    return ADDON_APPS.map((app) => {
      if (app.id === 'paypal') {
        return {
          ...app,
          plans: paypalPlans ?? app.plans,
          currentPlanId: currentPaypalPlan ?? app.currentPlanId,
          isInstalled: paypalInstalled,
        };
      }
      if (app.id === 'returns') {
        return {
          ...app,
          plans: returnsPlans ?? app.plans,
          currentPlanId: currentReturnsPlan ?? app.currentPlanId,
          isInstalled: returnsInstalled,
        };
      }
      return app;
    });
  }, [paypalPlans, returnsPlans, currentPaypalPlan, currentReturnsPlan, paypalInstalled, returnsInstalled]);

  // Auto-select a default plan once data loads: prefer the user's current plan
  // (if it exists in the visible list), otherwise fall back to the first plan.
  useEffect(() => {
    const plans = dynamicTrakowApp.plans;
    if (!plans?.length) return;
    setPlanSelections((prev) => {
      if (prev.trakow && plans.some((p) => p.id === prev.trakow)) return prev;
      const apiCurrent = planData?.data?.plan;
      const fallback = apiCurrent && plans.some((p) => p.id === apiCurrent) ? apiCurrent : plans[0].id;
      return { ...prev, trakow: fallback };
    });
  }, [dynamicTrakowApp.plans, planData?.data?.plan]);

  useEffect(() => {
    if (!paypalPlans?.length) return;
    setPlanSelections((prev) => {
      if (prev.paypal && paypalPlans.some((p) => p.id === prev.paypal)) return prev;
      const fallback =
        currentPaypalPlan && paypalPlans.some((p) => p.id === currentPaypalPlan) ? currentPaypalPlan : paypalPlans[0].id;
      return { ...prev, paypal: fallback };
    });
  }, [paypalPlans, currentPaypalPlan]);

  useEffect(() => {
    if (!returnsPlans?.length) return;
    setPlanSelections((prev) => {
      if (prev.returns && returnsPlans.some((p) => p.id === prev.returns)) return prev;
      const fallback =
        currentReturnsPlan && returnsPlans.some((p) => p.id === currentReturnsPlan)
          ? currentReturnsPlan
          : returnsPlans[0].id;
      return { ...prev, returns: fallback };
    });
  }, [returnsPlans, currentReturnsPlan]);

  const toggleApp = (id: 'paypal' | 'returns') =>
    setAppStates((prev) => ({ ...prev, [id]: !prev[id] }));

  const setPlan = (id: 'trakow' | 'paypal' | 'returns', planId: string) =>
    setPlanSelections((prev) => ({ ...prev, [id]: planId }));

  return (
    <Page
      title="Post-purchase stack"
      backAction={{ content: 'Plans', onAction: onBack }}
    >
      <StyledBundleLayout>
        <StyledBundleMain>
          <CoBenefitBanner appStates={appStates} />
          <TrakowCard
            app={dynamicTrakowApp}
            selectedPlanId={planSelections.trakow}
            onChange={(pid) => setPlan('trakow', pid)}
            isLoading={isTrakowLoading}
          />
          {dynamicAddonApps.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              selectedPlanId={planSelections[app.id]}
              included={appStates[app.id]}
              onChange={(pid) => setPlan(app.id, pid)}
              onToggle={() => toggleApp(app.id)}
              isLoading={app.id === 'paypal' ? isPaypalLoading : isReturnsLoading}
            />
          ))}
        </StyledBundleMain>
        <StyledBundleAside>
          <SummaryCard
            appStates={appStates}
            planSelections={planSelections}
            trakowOverride={dynamicTrakowApp}
            addonOverrides={dynamicAddonApps}
            onChargeTrakow={handleChargeTrakow}
            isChargingTrakow={isChargingTrakow}
            shop={shop}
          />
        </StyledBundleAside>
      </StyledBundleLayout>
    </Page>
  );
};

export default BundleBuilder;
